import type { SearchParameters } from '../searchParameters';
import type { SearchProfile } from '../searchProfile';
import { isAppliedFilterExcluded } from './isExcluded';
import type {
  RawAppliedFilter,
  AppliedFilterSiteExclusions,
} from './appliedFilter';

const COLLECTION_PARAMS: readonly string[] = [
  'collections',
  'editorialsources',
];

// Flattens the raw parameter values into one applied filter per value: arrays
// fan out to one each, and empty or non-scalar values (an empty string, an unset
// optional, an object) contribute nothing.
const flatten = (
  values: ReturnType<SearchParameters['toJSON']>
): RawAppliedFilter[] => {
  const filters: RawAppliedFilter[] = [];
  Object.entries(values).forEach(([name, value]) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null && entry !== '') {
          filters.push({ name, value: String(entry) });
        }
      });
    } else if (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      typeof value !== 'object'
    ) {
      filters.push({ name, value: String(value) });
    }
  });
  return filters;
};

// collections and editorialsources collapse into a single "collections" entry
// whose value is the total number of selected collections. Each parameter holds
// its selections as a comma-joined string, so the count is the number of
// comma-separated codes across both.
const countSelections = (value: string): number =>
  value.split(',').filter(Boolean).length;

const mergeCollections = (filters: RawAppliedFilter[]): RawAppliedFilter[] => {
  const collections = filters.filter((filter) =>
    COLLECTION_PARAMS.includes(filter.name)
  );
  if (collections.length === 0) return filters;
  const count = collections.reduce(
    (total, filter) => total + countSelections(filter.value),
    0
  );
  return [
    ...filters.filter((filter) => !COLLECTION_PARAMS.includes(filter.name)),
    { name: 'collections', value: count.toString() },
  ];
};

// A min and a max video duration together read as one range, so the max is
// dropped and the min stands in for the pair.
const mergeDurations = (filters: RawAppliedFilter[]): RawAppliedFilter[] => {
  const durationCount = filters.filter((filter) =>
    filter.name.includes('videoduration')
  ).length;
  if (durationCount > 1) {
    return filters.filter((filter) => filter.name !== 'videodurationmax');
  }
  return filters;
};

// Derives the removable applied filters for a search, before the collection and
// video-duration merges are applied: the set parameter values, minus the ones
// that should not surface as chips. A cross-domain derivation (search parameters
// plus search profile), so it lives in the service rather than on either model.
// `raw` because display labels are derived separately in the presentation layer;
// this returns names and values only.
export const getRawAppliedFilters = (
  searchParameters: SearchParameters,
  searchProfile: SearchProfile,
  siteExclusions: AppliedFilterSiteExclusions
): RawAppliedFilter[] =>
  flatten(searchParameters.toJSON()).filter(
    (filter) =>
      !isAppliedFilterExcluded(
        filter,
        searchParameters,
        searchProfile,
        siteExclusions
      )
  );

// Derives the removable applied filters for a search with the collection and
// video-duration merges applied on top of `getRawAppliedFilters`. Still `raw`:
// display labels are added in the presentation layer.
export const getMergedRawAppliedFilters = (
  searchParameters: SearchParameters,
  searchProfile: SearchProfile,
  siteExclusions: AppliedFilterSiteExclusions
): RawAppliedFilter[] =>
  mergeDurations(
    mergeCollections(
      getRawAppliedFilters(searchParameters, searchProfile, siteExclusions)
    )
  );
